import type { ForwardedRef } from 'react';
import { forwardRef } from 'react';
import { useMeQuery } from '@frameio/graphql-client/generated';
import { useIsElementAtTopOfViewWithScrollEvent } from '@frameio/shared/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { Box, Text, useId } from '@frameio/vapor';
import { useResourcesGrantingAccess } from '../hooks/useResourcesGrantingAccess';
import { StyledDivForTitle, StyledList } from '../styles';
import type { InviteAccessMemberProps } from '../types';
import { InviteAccessResource } from './InviteAccessResource';

type InviteAccessResourceListProps = {
  disableResourceActions?: boolean;
  disableStickyHeader?: boolean;
  onChangeResource?: (id: string) => void;
  resourceId: string;
  resourceTypename: string;
  title?: string;
  trackingSource?: TrackingSourceType;
} & Pick<InviteAccessMemberProps, 'backgroundColor'>;

export const InviteAccessResourceList = forwardRef(
  (
    {
      backgroundColor,
      disableResourceActions,
      disableStickyHeader,
      onChangeResource,
      resourceId,
      resourceTypename,
      title,
      trackingSource,
    }: InviteAccessResourceListProps,
    scrollRef: ForwardedRef<HTMLElement>
  ) => {
    const { resourcesGrantingAccess } = useResourcesGrantingAccess(
      resourceId,
      resourceTypename
    );

    const scrollRefCurrent =
      scrollRef && typeof scrollRef !== 'function' ? scrollRef.current : null;
    const [titleRef, isScrolledToTop] =
      useIsElementAtTopOfViewWithScrollEvent(scrollRefCurrent);

    const { data: currentUser } = useMeQuery();
    const titleId = useId();

    if (!resourcesGrantingAccess?.length) {
      return null;
    }

    return (
      <>
        {title && (
          <StyledDivForTitle
            ref={titleRef}
            $backgroundColor={backgroundColor}
            $showShadow={isScrolledToTop && !disableStickyHeader}
            id={titleId}
          >
            <Text color="tertiary" variant="body">
              {title}
            </Text>
          </StyledDivForTitle>
        )}
        <Box
          aria-labelledby={titleId}
          as={StyledList}
          paddingTop={title ? 4 : undefined}
          paddingX={16}
          width="100%"
        >
          {resourcesGrantingAccess.map((resource) => (
            <Box key={resource.id} as="li">
              <InviteAccessResource
                currentResourceId={resourceId}
                currentUserId={currentUser?.me?.id}
                disableResourceActions={
                  !resource.permissions?.canEditUserPermissions ||
                  disableResourceActions
                }
                grantingAccessResourceId={resource.id}
                memberCount={resource.memberCount}
                onChangeResource={onChangeResource}
                resourceName={resource.name ?? ''}
                resourceType={resource.resourceType}
                trackingSource={trackingSource}
              />
            </Box>
          ))}
        </Box>
      </>
    );
  }
);
